import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import {
  collection,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  getDocs,
  getCountFromServer,
  QueryDocumentSnapshot,
  Query,
  QueryConstraint,
} from "firebase/firestore";
import { db } from "../firebase";

interface UseFirestorePaginationProps {
  collectionName: string;
  itemsPerPage?: number;
  sortField: string;
  sortOrder: "asc" | "desc";
  baseConstraints?: QueryConstraint[];
  filters?: Record<string, any>;
  isSearchMode?: boolean;
  enabled?: boolean;
}

export function useFirestorePagination<T extends { id?: string; ID?: string }>({
  collectionName,
  itemsPerPage = 20,
  sortField,
  sortOrder,
  baseConstraints, // remove default []
  filters, // remove default {}
  isSearchMode = false,
  enabled = true,
}: UseFirestorePaginationProps) {
  const [items, setItems] = useState<T[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalDocs, setTotalDocs] = useState(0);

  // Stable defaults for optional props to prevent infinite loops if caller passes undefined
  const safeBaseConstraints = useMemo(
    () => baseConstraints || [],
    [baseConstraints],
  );
  const safeFilters = useMemo(() => filters || {}, [filters]);

  const isMounted = useRef(true);
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  const pagesCache = useRef<
    Map<
      number,
      {
        items: T[];
        startDoc: QueryDocumentSnapshot | null;
        endDoc: QueryDocumentSnapshot | null;
      }
    >
  >(new Map());

  const buildBaseQuery = useCallback(() => {
    let q: Query = collection(db, collectionName);

    // Apply base constraints (roles, etc.)
    if (safeBaseConstraints.length > 0) {
      q = query(q, ...safeBaseConstraints);
    }

    // Apply UI filters
    Object.entries(safeFilters).forEach(([field, value]) => {
      if (value) {
        q = query(q, where(field, "==", value));
      }
    });

    return q;
  }, [collectionName, safeBaseConstraints, safeFilters]);

  const fetchTotalCount = useCallback(async () => {
    try {
      const q = buildBaseQuery();
      const snap = await getCountFromServer(q);
      if (!isMounted.current) return;
      const count = snap.data().count ?? 0;
      setTotalDocs(count);
      setTotalPages(Math.max(1, Math.ceil(count / itemsPerPage)));
    } catch (err) {
      console.error(
        `[useFirestorePagination] Failed to fetch total count for ${collectionName}:`,
        err,
      );
      if (isMounted.current) {
        setTotalDocs(0);
        setTotalPages(1);
      }
    }
  }, [buildBaseQuery, itemsPerPage, collectionName]);

  const fetchPageFromServer = useCallback(
    async (page: number) => {
      const cached = pagesCache.current.get(page);
      if (cached) return cached;

      try {
        let q = buildBaseQuery();
        q = query(q, orderBy(sortField, sortOrder));

        if (page === 1) {
          const snap = await getDocs(query(q, limit(itemsPerPage)));
          if (!isMounted.current)
            return { items: [], startDoc: null, endDoc: null };

          const docs = snap.docs as QueryDocumentSnapshot[];
          const pageItems = docs.map((d) => ({
            id: d.id,
            ID: d.id,
            ...(d.data() as any),
          })) as T[];
          const result = {
            items: pageItems,
            startDoc: docs[0] ?? null,
            endDoc: docs[docs.length - 1] ?? null,
          };
          pagesCache.current.set(1, result);
          return result;
        }

        // Ensure previous pages are fetched for cursors
        for (let p = 1; p < page; p++) {
          if (!pagesCache.current.has(p)) {
            await fetchPageFromServer(p);
          }
        }

        const prevPage = pagesCache.current.get(page - 1);
        if (!prevPage || !prevPage.endDoc) {
          const empty = { items: [], startDoc: null, endDoc: null };
          pagesCache.current.set(page, empty);
          return empty;
        }

        const pagedQuery = query(
          q,
          startAfter(prevPage.endDoc),
          limit(itemsPerPage),
        );
        const snap = await getDocs(pagedQuery);
        if (!isMounted.current)
          return { items: [], startDoc: null, endDoc: null };

        const docs = snap.docs as QueryDocumentSnapshot[];
        const pageItems = docs.map((d) => ({
          id: d.id,
          ID: d.id,
          ...(d.data() as any),
        })) as T[];
        const result = {
          items: pageItems,
          startDoc: docs[0] ?? null,
          endDoc: docs[docs.length - 1] ?? null,
        };
        pagesCache.current.set(page, result);
        return result;
      } catch (err) {
        console.error(
          `[useFirestorePagination] Error fetching page ${page} for ${collectionName}:`,
          err,
        );
        const empty = { items: [], startDoc: null, endDoc: null };
        pagesCache.current.set(page, empty);
        return empty;
      }
    },
    [buildBaseQuery, itemsPerPage, sortField, sortOrder, collectionName],
  );

  const showPage = useCallback(
    async (page: number) => {
      let p = Math.max(1, page);
      if (totalPages && p > totalPages) p = totalPages;

      const cached = pagesCache.current.get(p);
      if (cached) {
        setItems(cached.items);
        setCurrentPage(p);
        return;
      }

      setIsLoading(true);
      try {
        const res = await fetchPageFromServer(p);
        if (isMounted.current) {
          setItems(res.items);
          setCurrentPage(p);
        }
      } finally {
        if (isMounted.current) setIsLoading(false);
      }
    },
    [totalPages, fetchPageFromServer],
  );

  const clearCache = useCallback((keepPage = 1) => {
    pagesCache.current.clear();
    setCurrentPage(keepPage);
    setItems([]);
  }, []);

  const refresh = useCallback(async () => {
    if (isSearchMode || !enabled) return;
    setIsLoading(true);
    try {
      clearCache(1);
      await fetchTotalCount();
      const res = await fetchPageFromServer(1);
      if (isMounted.current) {
        setItems(res.items);
      }
    } finally {
      if (isMounted.current) setIsLoading(false);
    }
  }, [isSearchMode, clearCache, fetchTotalCount, fetchPageFromServer, enabled]);

  const updateItemInCache = useCallback(
    async (updatedItem: T) => {
      const itemId = updatedItem.id || updatedItem.ID;
      if (!itemId) return;

      let foundInCurrentPage = false;

      pagesCache.current.forEach((pageData, pageNum) => {
        const idx = pageData.items.findIndex(
          (item) => (item.id || item.ID) === itemId,
        );
        if (idx !== -1) {
          const updatedList = [...pageData.items];
          updatedList[idx] = { ...updatedList[idx], ...updatedItem };

          pagesCache.current.set(pageNum, {
            ...pageData,
            items: updatedList,
          });

          if (pageNum === currentPage) {
            setItems(updatedList);
            foundInCurrentPage = true;
          }
        }
      });

      // If sort field changed, might need refresh.
      // This logic is a bit complex for a generic hook,
      // but we can at least check if the current page was affected.
      if (foundInCurrentPage) {
        const currentItem = items.find(
          (item) => (item.id || item.ID) === itemId,
        );
        if (currentItem) {
          const newVal = (updatedItem as any)[sortField];
          const oldVal = (currentItem as any)[sortField];
          if (newVal !== oldVal) {
            // Sort value changed, might need refresh to maintain order
            // refresh(); // Optional: trigger a full refresh or just current page
          }
        }
      }
    },
    [currentPage, items, sortField],
  );

  useEffect(() => {
    if (enabled) {
      refresh();
    }
  }, [refresh, enabled]);

  return {
    items,
    isLoading,
    currentPage,
    totalPages,
    totalDocs,
    showPage,
    refresh,
    updateItemInCache,
    clearCache,
    setIsLoading,
    setItems,
    setTotalDocs,
    setTotalPages,
    setCurrentPage,
  };
}
